import { useEffect } from "react";
import { Check, Loader2 } from "lucide-react";
import type { MessageSummaryDto } from "../../../shared/types";
import { useAiGroupEmails, useFilters, usePinEmail } from "../../hooks/useApi";
import { SWATCH_CLASSES } from "../filterColors";

/**
 * "Add email to AI category": pick a group and the email is pinned to it —
 * stored in ai_group_email and enforced as a matched verdict, so the AI test
 * is skipped for this email from now on.
 */
export function AddToAiGroupDialog({
  message,
  accountId,
  onClose,
}: {
  message: MessageSummaryDto;
  /** The mail view's account id (0 = combined). */
  accountId: number;
  onClose: () => void;
}) {
  const { data: filters } = useFilters();
  const { data: pins } = useAiGroupEmails();
  const pinEmail = usePinEmail();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Combined ids are "accountId:rawId" — the pin API wants the raw parts.
  const realAccountId = message.origin?.accountId ?? accountId;
  const rawMessageId =
    message.origin && message.id.startsWith(`${message.origin.accountId}:`)
      ? message.id.slice(`${message.origin.accountId}:`.length)
      : message.id;

  const pinnedGroups = new Set(
    (pins ?? [])
      .filter((p) => p.accountId === realAccountId && p.messageId === rawMessageId)
      .map((p) => p.filterId),
  );

  const groups = [...(filters ?? [])].sort((a, b) => a.order - b.order);

  const pick = (filterId: number) => {
    pinEmail.mutate(
      { filterId, accountId: realAccountId, messageId: rawMessageId },
      { onSuccess: onClose },
    );
  };

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/30"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-[400px] max-w-[92vw] rounded-lg border border-stroke bg-white p-5 shadow-xl"
        role="dialog"
        aria-label="Add email to AI category"
      >
        <h2 className="text-[15px] font-semibold">Add email to AI category</h2>
        <p className="truncate pt-1 text-[12px] text-ink-muted">
          {message.from?.name ?? "Unknown sender"} — {message.subject}
        </p>

        <div className="flex flex-col gap-1 pt-3">
          {groups.map((f) => {
            const pinned = pinnedGroups.has(f.id);
            return (
              <button
                key={f.id}
                onClick={() => !pinned && pick(f.id)}
                disabled={pinned || pinEmail.isPending}
                className={`flex items-center gap-2.5 rounded px-3 py-1.5 text-left text-[13px] ${
                  pinned ? "cursor-default text-ink-muted" : "hover:bg-accent-subtle"
                }`}
              >
                <span className={`size-2.5 shrink-0 rounded-full ${SWATCH_CLASSES[f.color]}`} />
                <span className="min-w-0 flex-1 truncate">{f.name}</span>
                {pinned && <Check size={14} className="text-green-700" />}
              </button>
            );
          })}
          {!groups.length && (
            <p className="text-[12px] text-ink-muted">No AI groups yet — create one in Settings.</p>
          )}
        </div>

        {pinEmail.isPending && (
          <p className="flex items-center gap-1.5 pt-2 text-[12px] text-ink-muted">
            <Loader2 size={12} className="animate-spin" /> Pinning…
          </p>
        )}
        {pinEmail.error && (
          <p className="pt-2 text-[12px] text-red-700">{pinEmail.error.message}</p>
        )}

        <div className="flex justify-end pt-3">
          <button
            onClick={onClose}
            className="rounded border border-stroke px-4 py-1.5 text-[13px] hover:bg-black/5"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
